/**
 * Non-TPM Devices Management Page
 *
 * Shows devices with TPM hardware failures and allows admin to grant temporary access
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import {
  useGetNonTpmConnectionAttemptsQuery,
  useGetNonTpmStatisticsQuery,
  useGrantNonTpmAccessMutation,
  useRevokeNonTpmAccessMutation,
  NonTPMConnectionAttempt,
} from '@store/api/nonTpmApi';
import errorService from '@services/errorService';
import { useQueryError } from '@hooks/useQueryError';
import {
  useTable,
  SortingState,
} from '@tanstack/react-table';
import { features } from '../components/common/tableFeatures';
import ColumnSelector from '../components/common/ColumnSelector';
import {
  DataTable,
  DataTableToolbar,
} from '../components/common/DataTable';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { createNonTpmColumns } from '../components/non-tpm/nonTpmColumns';
import {
  NON_TPM_COLUMN_METADATA,
  NON_TPM_DEFAULT_COLUMNS,
} from '../components/non-tpm/nonTpmColumnMetadata';

const NonTPMDevicesPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [showUnreviewedOnly, setShowUnreviewedOnly] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);

  const {
    data: attempts = [],
    isFetching: loading,
    error: attemptsError,
    refetch: refetchAttempts,
  } = useGetNonTpmConnectionAttemptsQuery({ unreviewed_only: showUnreviewedOnly });
  const {
    data: statistics,
    error: statsError,
    refetch: refetchStats,
  } = useGetNonTpmStatisticsQuery();

  const [grantAccess, { isLoading: grantLoading }] = useGrantNonTpmAccessMutation();
  const [revokeAccess] = useRevokeNonTpmAccessMutation();

  const queryError = useQueryError(attemptsError || statsError);
  const displayError = error ?? queryError;

  // Grant access dialog
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<NonTPMConnectionAttempt | null>(null);
  const [durationHours, setDurationHours] = useState(24);
  const [reason, setReason] = useState('');

  const loadData = () => {
    setError(null);
    refetchAttempts();
    refetchStats();
  };

  const handleGrantAccess = (attempt: NonTPMConnectionAttempt) => {
    setSelectedDevice(attempt);
    setReason('');
    setDurationHours(24);
    setGrantDialogOpen(true);
  };

  const handleConfirmGrant = async () => {
    if (!selectedDevice || !reason.trim()) {
      return;
    }

    try {
      await grantAccess({
        deviceId: selectedDevice.device_id,
        request: {
          duration_hours: durationHours,
          reason: reason.trim(),
          auto_create_device: true,
        },
      }).unwrap();

      setGrantDialogOpen(false);
      setSelectedDevice(null);
    } catch (err) {
      setError(errorService.toUserMessage(err));
    }
  };

  const handleRevokeAccess = async (deviceId: string) => {
    if (!confirm('Are you sure you want to revoke access for this device?')) {
      return;
    }

    try {
      await revokeAccess(deviceId).unwrap();
    } catch (err) {
      setError(errorService.toUserMessage(err));
    }
  };

  const {
    visibleColumns,
    toggleColumn,
    resetToDefault,
    applyPreset,
    columnVisibility,
  } = useColumnVisibility({
    columns: NON_TPM_COLUMN_METADATA,
    storageKey: 'nonTpmDevices.columnVisibility.v1',
    defaultColumns: NON_TPM_DEFAULT_COLUMNS,
  });


  // Cell renderers close over the grant/revoke handlers.
  const columns = useMemo(
    () =>
      createNonTpmColumns({
        onGrantAccess: handleGrantAccess,
        onRevokeAccess: handleRevokeAccess,
      }),
    // Safe to build once: the handlers only touch state setters and RTK
    // mutation triggers, all of which keep their identity across renders.
    []
  );

  const table = useTable({
    features,
    data: attempts,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getRowId: (row) => row.id,
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Non-TPM Devices
        </Typography>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={showUnreviewedOnly}
                onChange={(e) => setShowUnreviewedOnly(e.target.checked)}
              />
            }
            label="Unreviewed Only"
          />
          <IconButton onClick={loadData} disabled={loading} aria-label="Refresh non-TPM device list">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {displayError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {displayError}
        </Alert>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{statistics.total_attempts}</Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>Total Attempts</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{
                color: 'warning.main'
              }}>{statistics.unreviewed}</Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>Unreviewed</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{
                color: 'success.main'
              }}>{statistics.active_access}</Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>Active Access</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{
                color: 'error.main'
              }}>{statistics.expired_access}</Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>Expired</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{statistics.unique_devices}</Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>Unique Devices</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: statistics.requires_attention > 0 ? 'warning.light' : 'background.paper' }}>
              <Typography variant="h4" sx={{ color: statistics.requires_attention > 0 ? 'warning.contrastText' : 'text.primary' }}>
                {statistics.requires_attention}
              </Typography>
              <Typography variant="body2" sx={{ color: statistics.requires_attention > 0 ? 'warning.contrastText' : 'text.secondary' }}>
                Needs Attention
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Connection Attempts Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connection Attempts
          </Typography>

          <DataTableToolbar
            actions={
              <ColumnSelector
                columns={NON_TPM_COLUMN_METADATA}
                visibleColumns={visibleColumns}
                onColumnToggle={toggleColumn}
                onReset={resetToDefault}
                onPresetSelect={applyPreset}
                />
            }
          />

          <DataTable
            table={table}
            ariaLabel="Non-TPM connection attempts"
            isLoading={loading}
            emptyMessage={`No ${showUnreviewedOnly ? 'unreviewed ' : ''}connection attempts found`}
          />
        </CardContent>
      </Card>

      {/* Grant Access Dialog */}
      <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Temporary Access</DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will grant temporary non-TPM access to: <strong>{selectedDevice.device_id}</strong>
              </Alert>

              <TextField
                label="Duration (hours)"
                type="number"
                value={durationHours}
                onChange={(e) => setDurationHours(Math.max(1, Math.min(168, parseInt(e.target.value) || 24)))}
                fullWidth
                margin="normal"
                helperText="1-168 hours (default: 24)"
                slotProps={{
                  htmlInput: { min: 1, max: 168 }
                }}
              />

              <TextField
                label="Reason *"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                required
                helperText="Explain why this device needs non-TPM access (required for audit trail)"
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{
                  color: 'text.secondary'
                }}>
                  <strong>Device Info:</strong>
                </Typography>
                <Typography variant="body2">MAC: {selectedDevice.mac_address}</Typography>
                {selectedDevice.hardware_model && (
                  <Typography variant="body2">Model: {selectedDevice.hardware_model}</Typography>
                )}
                <Typography variant="body2" sx={{
                  color: 'error.main'
                }}>
                  TPM Error: {selectedDevice.tpm_error_message}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmGrant}
            color="primary"
            variant="contained"
            disabled={grantLoading || !reason.trim()}
          >
            {grantLoading ? 'Granting...' : 'Grant Access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NonTPMDevicesPage;
